import { openDB, type DBSchema, type IDBPDatabase } from 'idb';
import { z } from 'zod';

import {
  MAX_PUZZLE_SOLUTION_PLIES,
  createPuzzleState,
  reducePuzzle,
  type PuzzleDefinition,
  type PuzzleState,
  type PuzzleStatus,
} from '../puzzles/puzzleDomain';

export const PUZZLE_PROGRESS_RECORD_VERSION = 1;
/**
 * Bump when the exact bundled starter-puzzle catalog changes. This makes a
 * record's progress meaningful only against the pack that created it.
 */
export const PUZZLE_PROGRESS_CATALOG_VERSION = 1;
export const PUZZLE_PROGRESS_PERSISTENCE_DATABASE = 'capychess-puzzle-progress';
export const PUZZLE_PROGRESS_PERSISTENCE_DATABASE_VERSION = 1;

const PROGRESS_STORE = 'puzzle-progress';
const CURRENT_PROGRESS_KEY = 'current';
const PROGRESS_ENVELOPE_VERSION = 1;
const UCI_PATTERN = /^[a-h][1-8][a-h][1-8][qrbn]?$/;

const puzzleStatusSchema = z.enum(['active', 'try-again', 'solved', 'revealed']);
const uciMoveSchema = z.string().regex(UCI_PATTERN, 'Expected a lowercase UCI move.');
const nonNegativeSafeIntegerSchema = z.number().refine(
  (value) => Number.isSafeInteger(value) && value >= 0,
  'Expected a non-negative safe integer.',
);

const puzzleProgressEntrySchema = z.object({
  puzzleId: z.string().min(1),
  acceptedUciMoves: z.array(uciMoveSchema).max(MAX_PUZZLE_SOLUTION_PLIES),
  status: puzzleStatusSchema,
  hintStage: nonNegativeSafeIntegerSchema,
}).strict();

/**
 * This record deliberately stores no FEN, player name, or other identity.
 * Every position is reconstructed from a bundled puzzle definition and its
 * accepted UCI history before it can be restored into the UI.
 */
export const PuzzleProgressRecordSchema = z.object({
  schemaVersion: z.literal(PUZZLE_PROGRESS_RECORD_VERSION),
  catalogVersion: z.literal(PUZZLE_PROGRESS_CATALOG_VERSION),
  activePuzzleId: z.string().min(1),
  entries: z.array(puzzleProgressEntrySchema).min(1).max(128),
}).strict().superRefine((record, context) => {
  const seen = new Set<string>();
  for (const [index, entry] of record.entries.entries()) {
    if (seen.has(entry.puzzleId)) {
      context.addIssue({
        code: 'custom',
        path: ['entries', index, 'puzzleId'],
        message: 'Puzzle progress may include each puzzle only once.',
      });
    }
    seen.add(entry.puzzleId);
  }

  if (!seen.has(record.activePuzzleId)) {
    context.addIssue({
      code: 'custom',
      path: ['activePuzzleId'],
      message: 'The active puzzle must have a saved progress entry.',
    });
  }
});

export interface PuzzleProgressEntry {
  readonly puzzleId: string;
  readonly acceptedUciMoves: readonly string[];
  readonly status: PuzzleStatus;
  readonly hintStage: number;
}

export interface PuzzleProgressRecord {
  readonly schemaVersion: typeof PUZZLE_PROGRESS_RECORD_VERSION;
  readonly catalogVersion: typeof PUZZLE_PROGRESS_CATALOG_VERSION;
  readonly activePuzzleId: string;
  readonly entries: readonly PuzzleProgressEntry[];
}

export interface RestoredPuzzleProgress {
  readonly activePuzzleId: string;
  readonly states: readonly PuzzleState[];
}

export type RestorePuzzleProgressResult =
  | { readonly ok: true; readonly value: RestoredPuzzleProgress }
  | { readonly ok: false };

export type PuzzleProgressPersistenceFailure =
  | { readonly kind: 'invalid-record' }
  | { readonly kind: 'conflict'; readonly operation: 'write' }
  | { readonly kind: 'quota'; readonly operation: 'read' | 'write'; readonly cause: unknown }
  | { readonly kind: 'security'; readonly operation: 'read' | 'write'; readonly cause: unknown }
  | { readonly kind: 'read-failed'; readonly cause: unknown }
  | { readonly kind: 'write-failed'; readonly cause: unknown };

export type ReadPuzzleProgressResult =
  | { readonly ok: true; readonly value?: PuzzleProgressRecord }
  | {
      readonly ok: false;
      readonly error: PuzzleProgressPersistenceFailure;
      readonly fallback: PuzzleProgressRecord | undefined;
    };

export type SavePuzzleProgressResult =
  | { readonly ok: true }
  | { readonly ok: false; readonly error: PuzzleProgressPersistenceFailure };

export type FlushPuzzleProgressResult = SavePuzzleProgressResult;

/** Result of an explicitly confirmed attempt to discard only unusable progress. */
export type ResetInvalidPuzzleProgressResult = SavePuzzleProgressResult;

export interface IndexedDbPuzzleProgressPersistenceOptions {
  readonly databaseName?: string;
}

type CurrentProgressExpectation =
  | { readonly kind: 'empty' }
  | { readonly kind: 'generation'; readonly generation: number }
  | { readonly kind: 'unavailable' };

type ParsedCurrentProgress =
  | {
      readonly kind: 'empty';
      readonly expectation: Extract<CurrentProgressExpectation, { readonly kind: 'empty' }>;
    }
  | {
      readonly kind: 'generation';
      readonly expectation: Extract<CurrentProgressExpectation, { readonly kind: 'generation' }>;
      readonly generation: number;
      readonly record: PuzzleProgressRecord;
    }
  | { readonly kind: 'invalid' };

const currentProgressEnvelopeSchema = z.object({
  envelopeVersion: z.literal(PROGRESS_ENVELOPE_VERSION),
  generation: z.number().refine(
    (value) => Number.isSafeInteger(value) && value > 0 && value < Number.MAX_SAFE_INTEGER,
    'Expected a generation that can be incremented safely.',
  ),
  record: PuzzleProgressRecordSchema,
}).strict();

interface PuzzleProgressPersistenceDatabase extends DBSchema {
  [PROGRESS_STORE]: {
    key: typeof CURRENT_PROGRESS_KEY;
    value: unknown;
  };
}

/**
 * Builds the only serializable progress shape the app writes. Puzzle FENs are
 * intentionally absent: they are bundled content, not learner state.
 */
export function createPuzzleProgressRecord(
  activePuzzleId: string,
  states: readonly PuzzleState[],
): PuzzleProgressRecord {
  return parseRecord({
    schemaVersion: PUZZLE_PROGRESS_RECORD_VERSION,
    catalogVersion: PUZZLE_PROGRESS_CATALOG_VERSION,
    activePuzzleId,
    entries: states.map((state) => ({
      puzzleId: state.puzzle.id,
      acceptedUciMoves: state.moves.map((move) => move.uci),
      status: state.status,
      hintStage: state.hintStage,
    })),
  }) ?? (() => {
    throw new Error('Puzzle progress could not be serialized from a valid puzzle state.');
  })();
}

/**
 * Replays untrusted persisted progress through the existing puzzle reducer.
 * A structural record only becomes usable state when its moves are a valid
 * canonical prefix of the packaged solution and all transient state can be
 * derived again. Invalid input fails closed without touching storage.
 */
export function restorePuzzleProgress(
  input: unknown,
  puzzles: readonly PuzzleDefinition[],
): RestorePuzzleProgressResult {
  const record = parseRecord(input);
  if (record === undefined || puzzles.length === 0) {
    return { ok: false };
  }

  const puzzleById = new Map<string, PuzzleDefinition>();
  for (const puzzle of puzzles) {
    if (puzzleById.has(puzzle.id)) {
      return { ok: false };
    }
    puzzleById.set(puzzle.id, puzzle);
  }
  if (!puzzleById.has(record.activePuzzleId)) {
    return { ok: false };
  }

  const entryByPuzzleId = new Map(record.entries.map((entry) => [entry.puzzleId, entry]));
  if (entryByPuzzleId.size !== puzzleById.size) {
    return { ok: false };
  }
  for (const puzzleId of entryByPuzzleId.keys()) {
    if (!puzzleById.has(puzzleId)) {
      return { ok: false };
    }
  }

  const states: PuzzleState[] = [];
  for (const puzzle of puzzles) {
    const entry = entryByPuzzleId.get(puzzle.id);
    if (entry === undefined) {
      return { ok: false };
    }
    const restored = restorePuzzleState(puzzle, entry);
    if (restored === undefined) {
      return { ok: false };
    }
    states.push(restored);
  }

  return {
    ok: true,
    value: Object.freeze({
      activePuzzleId: record.activePuzzleId,
      states: Object.freeze(states),
    }),
  };
}

/**
 * IndexedDB adapter for one local, non-identity puzzle-progress record. It
 * follows the game-session generation guard so a corrupt or concurrently
 * changed value is never replaced implicitly.
 */
export class IndexedDbPuzzleProgressPersistence {
  private readonly databaseName: string;
  private databasePromise: Promise<IDBPDatabase<PuzzleProgressPersistenceDatabase>> | undefined;
  private expectedCurrent: CurrentProgressExpectation = { kind: 'empty' };
  private lastValidCurrent: PuzzleProgressRecord | undefined;
  /** Keeps autosave effects ordered before the generation guard evaluates them. */
  private writeQueue: Promise<void> = Promise.resolve();
  /** A failed write remains visible until a validated read establishes a new base. */
  private queuedWriteFailure: PuzzleProgressPersistenceFailure | undefined;

  public constructor(options: IndexedDbPuzzleProgressPersistenceOptions = {}) {
    this.databaseName = options.databaseName ?? PUZZLE_PROGRESS_PERSISTENCE_DATABASE;
  }

  public async read(puzzles: readonly PuzzleDefinition[]): Promise<ReadPuzzleProgressResult> {
    let rawCurrent: unknown;
    try {
      const database = await this.getDatabase();
      const transaction = database.transaction(PROGRESS_STORE, 'readonly');
      rawCurrent = await transaction.objectStore(PROGRESS_STORE).get(CURRENT_PROGRESS_KEY);
      await transaction.done;
    } catch (cause) {
      this.expectedCurrent = { kind: 'unavailable' };
      return {
        ok: false,
        error: toStorageFailure('read', cause),
        fallback: copyOptionalRecord(this.lastValidCurrent),
      };
    }

    const current = parseCurrentProgress(rawCurrent);
    if (current.kind === 'invalid' || (
      current.kind === 'generation' && !restorePuzzleProgress(current.record, puzzles).ok
    )) {
      this.expectedCurrent = { kind: 'unavailable' };
      return {
        ok: false,
        error: { kind: 'invalid-record' },
        fallback: copyOptionalRecord(this.lastValidCurrent),
      };
    }

    if (current.kind === 'empty') {
      this.expectedCurrent = current.expectation;
      this.lastValidCurrent = undefined;
      this.queuedWriteFailure = undefined;
      return { ok: true };
    }

    this.expectedCurrent = current.expectation;
    this.lastValidCurrent = current.record;
    this.queuedWriteFailure = undefined;
    return { ok: true, value: copyRecord(current.record) };
  }

  public save(
    input: unknown,
    puzzles: readonly PuzzleDefinition[],
  ): Promise<SavePuzzleProgressResult> {
    const record = parseRecord(input);
    if (record === undefined || !restorePuzzleProgress(record, puzzles).ok) {
      return Promise.resolve({ ok: false, error: { kind: 'invalid-record' } });
    }

    const operation = this.writeQueue.then(() => this.saveValidatedRecord(record, puzzles));
    this.writeQueue = operation.then(
      (result) => {
        if (!result.ok) {
          this.queuedWriteFailure = result.error;
        }
      },
      (cause: unknown) => {
        this.queuedWriteFailure = { kind: 'write-failed', cause };
      },
    );
    return operation;
  }

  /**
   * Deletes only the current puzzle-progress record after an explicit user
   * recovery action. The record is reread in the same write transaction, so a
   * concurrent tab that has repaired or replaced it with valid progress wins.
   */
  public resetInvalid(
    puzzles: readonly PuzzleDefinition[],
  ): Promise<ResetInvalidPuzzleProgressResult> {
    const operation = this.writeQueue.then(() => this.resetStillInvalidProgress(puzzles));
    this.writeQueue = operation.then(
      (result) => {
        if (!result.ok) {
          this.queuedWriteFailure = result.error;
        }
      },
      (cause: unknown) => {
        this.queuedWriteFailure = { kind: 'write-failed', cause };
      },
    );
    return operation;
  }

  /**
   * Waits for every queued write and preserves an observed failure for the
   * caller that needs a safe reload/update handoff.
   */
  public async flush(): Promise<FlushPuzzleProgressResult> {
    await this.writeQueue;
    return this.queuedWriteFailure === undefined
      ? { ok: true }
      : { ok: false, error: this.queuedWriteFailure };
  }

  private async saveValidatedRecord(
    record: PuzzleProgressRecord,
    puzzles: readonly PuzzleDefinition[],
  ): Promise<SavePuzzleProgressResult> {
    if (this.expectedCurrent.kind === 'unavailable') {
      return { ok: false, error: { kind: 'conflict', operation: 'write' } };
    }

    try {
      const database = await this.getDatabase();
      const transaction = database.transaction(PROGRESS_STORE, 'readwrite');
      const store = transaction.objectStore(PROGRESS_STORE);
      const actual = parseCurrentProgress(await store.get(CURRENT_PROGRESS_KEY));
      if (
        actual.kind === 'invalid'
        || !currentMatchesExpectation(actual, this.expectedCurrent)
        || (actual.kind === 'generation' && !restorePuzzleProgress(actual.record, puzzles).ok)
      ) {
        this.expectedCurrent = { kind: 'unavailable' };
        await transaction.done;
        return { ok: false, error: { kind: 'conflict', operation: 'write' } };
      }

      const generation = nextGenerationFor(actual);
      await store.put({
        envelopeVersion: PROGRESS_ENVELOPE_VERSION,
        generation,
        record: copyRecord(record),
      }, CURRENT_PROGRESS_KEY);
      await transaction.done;
      this.expectedCurrent = { kind: 'generation', generation };
      this.lastValidCurrent = copyRecord(record);
      return { ok: true };
    } catch (cause) {
      return { ok: false, error: toStorageFailure('write', cause) };
    }
  }

  private async resetStillInvalidProgress(
    puzzles: readonly PuzzleDefinition[],
  ): Promise<ResetInvalidPuzzleProgressResult> {
    try {
      const database = await this.getDatabase();
      const transaction = database.transaction(PROGRESS_STORE, 'readwrite');
      const store = transaction.objectStore(PROGRESS_STORE);
      const actual = parseCurrentProgress(await store.get(CURRENT_PROGRESS_KEY));
      const stillInvalid = actual.kind === 'invalid'
        || (actual.kind === 'generation' && !restorePuzzleProgress(actual.record, puzzles).ok);
      if (!stillInvalid) {
        this.expectedCurrent = { kind: 'unavailable' };
        await transaction.done;
        return { ok: false, error: { kind: 'conflict', operation: 'write' } };
      }

      await store.delete(CURRENT_PROGRESS_KEY);
      await transaction.done;
      this.expectedCurrent = { kind: 'empty' };
      this.lastValidCurrent = undefined;
      this.queuedWriteFailure = undefined;
      return { ok: true };
    } catch (cause) {
      return { ok: false, error: toStorageFailure('write', cause) };
    }
  }

  public close(): void {
    this.databasePromise?.then((database) => database.close()).catch(() => undefined);
    this.databasePromise = undefined;
  }

  private getDatabase(): Promise<IDBPDatabase<PuzzleProgressPersistenceDatabase>> {
    if (this.databasePromise === undefined) {
      const opening = openDB<PuzzleProgressPersistenceDatabase>(
        this.databaseName,
        PUZZLE_PROGRESS_PERSISTENCE_DATABASE_VERSION,
        {
          upgrade(database) {
            if (!database.objectStoreNames.contains(PROGRESS_STORE)) {
              database.createObjectStore(PROGRESS_STORE);
            }
          },
        },
      );
      this.databasePromise = opening;
      void opening.catch(() => {
        if (this.databasePromise === opening) {
          this.databasePromise = undefined;
        }
      });
    }
    return this.databasePromise;
  }
}

export function createIndexedDbPuzzleProgressPersistence(
  options?: IndexedDbPuzzleProgressPersistenceOptions,
): IndexedDbPuzzleProgressPersistence {
  return new IndexedDbPuzzleProgressPersistence(options);
}

function restorePuzzleState(
  puzzle: PuzzleDefinition,
  entry: PuzzleProgressEntry,
): PuzzleState | undefined {
  let state = createPuzzleState(puzzle);
  while (state.moves.length < entry.acceptedUciMoves.length) {
    const expectedUci = puzzle.solutionUci[state.solutionPly];
    if (expectedUci === undefined || entry.acceptedUciMoves[state.moves.length] !== expectedUci) {
      return undefined;
    }
    const progressed = reducePuzzle(state, { type: 'attempt-move', uci: expectedUci });
    if (progressed === state || progressed.moves.length > entry.acceptedUciMoves.length) {
      return undefined;
    }
    if (!isPrefixOf(entry.acceptedUciMoves, progressed.moves.map((move) => move.uci))) {
      return undefined;
    }
    state = progressed;
  }

  if (!sameMoves(entry.acceptedUciMoves, state.moves.map((move) => move.uci))) {
    return undefined;
  }

  if (entry.status === 'revealed') {
    if (entry.hintStage !== 0 || !sameMoves(entry.acceptedUciMoves, puzzle.solutionUci)) {
      return undefined;
    }
    const revealed = reducePuzzle(createPuzzleState(puzzle), { type: 'reveal-solution' });
    return revealed.status === 'revealed' ? revealed : undefined;
  }

  if (entry.status === 'try-again') {
    if (state.status !== 'active') {
      return undefined;
    }
  } else if (entry.status !== state.status) {
    return undefined;
  }

  if (entry.hintStage > 0) {
    if (entry.status !== 'active' && entry.status !== 'try-again') {
      return undefined;
    }
    for (let stage = 0; stage < entry.hintStage; stage += 1) {
      const hinted = reducePuzzle(state, { type: 'request-hint' });
      if (hinted === state) {
        return undefined;
      }
      state = hinted;
    }
  }

  if (entry.status === 'try-again') {
    return {
      ...state,
      revision: state.revision + 1,
      status: 'try-again',
      result: 'try-again',
    };
  }
  return state;
}

function isPrefixOf(expected: readonly string[], actual: readonly string[]): boolean {
  return actual.length <= expected.length && actual.every((move, index) => move === expected[index]);
}

function sameMoves(left: readonly string[], right: readonly string[]): boolean {
  return left.length === right.length && left.every((move, index) => move === right[index]);
}

function parseRecord(value: unknown): PuzzleProgressRecord | undefined {
  const parsed = PuzzleProgressRecordSchema.safeParse(value);
  if (!parsed.success) {
    return undefined;
  }
  return copyRecord({
    schemaVersion: parsed.data.schemaVersion,
    catalogVersion: parsed.data.catalogVersion,
    activePuzzleId: parsed.data.activePuzzleId,
    entries: parsed.data.entries.map((entry) => ({
      puzzleId: entry.puzzleId,
      acceptedUciMoves: entry.acceptedUciMoves,
      status: entry.status,
      hintStage: entry.hintStage,
    })),
  });
}

function parseCurrentProgress(value: unknown): ParsedCurrentProgress {
  if (value === undefined) {
    return { kind: 'empty', expectation: { kind: 'empty' } };
  }
  const envelope = currentProgressEnvelopeSchema.safeParse(value);
  if (!envelope.success) {
    return { kind: 'invalid' };
  }
  const record = parseRecord(envelope.data.record);
  if (record === undefined) {
    return { kind: 'invalid' };
  }
  return {
    kind: 'generation',
    expectation: { kind: 'generation', generation: envelope.data.generation },
    generation: envelope.data.generation,
    record,
  };
}

function currentMatchesExpectation(
  actual: ParsedCurrentProgress,
  expected: CurrentProgressExpectation,
): boolean {
  if (actual.kind === 'invalid' || expected.kind === 'unavailable' || actual.kind !== expected.kind) {
    return false;
  }
  return actual.kind === 'empty'
    || (actual.kind === 'generation' && expected.kind === 'generation' && actual.generation === expected.generation);
}

function nextGenerationFor(current: ParsedCurrentProgress): number {
  return current.kind === 'generation' ? current.generation + 1 : 1;
}

function copyRecord(record: PuzzleProgressRecord): PuzzleProgressRecord {
  return Object.freeze({
    schemaVersion: record.schemaVersion,
    catalogVersion: record.catalogVersion,
    activePuzzleId: record.activePuzzleId,
    entries: Object.freeze(record.entries.map((entry) => Object.freeze({
      puzzleId: entry.puzzleId,
      acceptedUciMoves: Object.freeze([...entry.acceptedUciMoves]),
      status: entry.status,
      hintStage: entry.hintStage,
    }))),
  });
}

function copyOptionalRecord(record: PuzzleProgressRecord | undefined): PuzzleProgressRecord | undefined {
  return record === undefined ? undefined : copyRecord(record);
}

function toStorageFailure(
  operation: 'read' | 'write',
  cause: unknown,
): PuzzleProgressPersistenceFailure {
  const name = errorName(cause);
  if (name === 'QuotaExceededError' || name === 'NS_ERROR_DOM_QUOTA_REACHED') {
    return { kind: 'quota', operation, cause };
  }
  if (name === 'SecurityError') {
    return { kind: 'security', operation, cause };
  }
  return operation === 'read'
    ? { kind: 'read-failed', cause }
    : { kind: 'write-failed', cause };
}

function errorName(cause: unknown): string | undefined {
  try {
    return cause !== null && typeof cause === 'object' && 'name' in cause && typeof cause.name === 'string'
      ? cause.name
      : undefined;
  } catch {
    return undefined;
  }
}
